import {
  AfterViewInit,
  Component, computed, DestroyRef, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { getFeedback } from '@sentry/angular';
import { Environment, NimmstaCommonsModuleConfiguration, ServiceType } from '../../nimmsta-commons-module.config';
import { Version } from '../../models/version';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { OfflineToastComponent } from '../offline-toast/offline-toast.component';
import { SENTRY_FEEDBACK_TRANSLATION_KEYS } from '../../utils/sentry';

const LARGE_BREAKPOINT = 992;

@Component({
    selector: 'app-default-layout',
    templateUrl: './default-layout.component.html',
    styleUrls: ['./default-layout.component.scss'],
    imports: [RouterLink, RouterLinkActive, RouterOutlet, TranslatePipe, OfflineToastComponent],
})
export class DefaultLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  collapsed = true;

  mobileExpanded = false;

  environment = signal<Environment | undefined>(undefined);

  Environment = Environment;

  private router = inject(Router);
  private configuration = inject(NimmstaCommonsModuleConfiguration);
  private translateService = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private detachSentryFeedback?: () => void;

  serviceType = this.configuration.serviceType;

  ServiceType = ServiceType;

  showEnvironment = computed(() => { const env = this.environment(); return !!env && env !== Environment.PROD; });

  @ViewChild('sentryButton')
    sentryReportButton!: ElementRef<HTMLButtonElement>;

  @Output()
    collapseState = new EventEmitter<boolean>();

  @Output()
    mobileExpandedState = new EventEmitter<boolean>();

  @Input()
    frontendVersion?: Version;

  @Input()
    backendVersion?: Version;

  ngOnInit(): void {
    const observer = new ResizeObserver(() => {
      // Ignore the fact that there might be multiple change elements. We check for the current state only anyway.
      this.checkCollapse();
    });
    observer.observe(document.querySelector('body')!);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.hideMobileNav();
      }
    });
    this.checkCollapse();
    // force initialisation of the collapse state
    this.collapseState.emit(this.collapsed);
    this.environment.set(this.configuration.environment);
  }

  ngAfterViewInit(): void {
    const feedback = getFeedback();
    if (feedback) {
      this.translateService
        .stream(SENTRY_FEEDBACK_TRANSLATION_KEYS)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((translations) => {
          this.detachSentryFeedback?.();
          this.detachSentryFeedback = feedback.attachTo(
            this.sentryReportButton.nativeElement,
            this.getSentryFeedbackText(translations),
          );
        });
    }
  }

  ngOnDestroy(): void {
    this.detachSentryFeedback?.();
  }

  private getSentryFeedbackText(translations: Record<string, string>) {
    return {
      triggerLabel: translations['sentry_feedback.trigger_label'],
      triggerAriaLabel: translations['sentry_feedback.trigger_aria_label'],
      cancelButtonLabel: translations['sentry_feedback.cancel_button_label'],
      submitButtonLabel: translations['sentry_feedback.submit_button_label'],
      confirmButtonLabel: translations['sentry_feedback.confirm_button_label'],
      formTitle: translations['sentry_feedback.form_title'],
      emailLabel: translations['sentry_feedback.email_label'],
      emailPlaceholder: translations['sentry_feedback.email_placeholder'],
      messageLabel: translations['sentry_feedback.message_label'],
      messagePlaceholder: translations['sentry_feedback.message_placeholder'],
      nameLabel: translations['sentry_feedback.name_label'],
      namePlaceholder: translations['sentry_feedback.name_placeholder'],
      successMessageText: translations['sentry_feedback.success_message_text'],
      isRequiredLabel: translations['sentry_feedback.is_required_label'],
      addScreenshotButtonLabel: translations['sentry_feedback.add_screenshot_button_label'],
      removeScreenshotButtonLabel: translations['sentry_feedback.remove_screenshot_button_label'],
      highlightToolText: translations['sentry_feedback.highlight_tool_text'],
      hideToolText: translations['sentry_feedback.hide_tool_text'],
      removeHighlightText: translations['sentry_feedback.remove_highlight_text'],
      errorEmptyMessageText: translations['sentry_feedback.error_empty_message_text'],
      errorNoClientText: translations['sentry_feedback.error_no_client_text'],
      errorTimeoutText: translations['sentry_feedback.error_timeout_text'],
      errorForbiddenText: translations['sentry_feedback.error_forbidden_text'],
      errorGenericText: translations['sentry_feedback.error_generic_text'],
    };
  }

  private checkCollapse() {
    const newCollapsed = window.innerWidth < LARGE_BREAKPOINT;
    if (this.collapsed !== newCollapsed) {
      this.setCollapseState(newCollapsed);
    }
  }

  private setCollapseState(collapsed: boolean) {
    this.collapsed = collapsed;
    this.collapseState.emit(collapsed);
  }

  toggleCollapse() {
    this.setCollapseState(!this.collapsed);
  }

  private hideMobileNav() {
    if (this.mobileExpanded) {
      this.toggleMobileNav();
    }
  }

  toggleMobileNav() {
    this.mobileExpanded = !this.mobileExpanded;
    this.mobileExpandedState.emit(this.mobileExpanded);
  }
}
